import { useContext, useEffect, useState } from 'react';
import { Grid, Container, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { IconButton } from '@mui/material';

import CustomerProfile from './CustomerProfile';
import Page from '../../../../components/Page';
import { StripeContextProvider } from '../../../../context/StripeContext';
import CONFIG from '@/config';
import {
  addRecentlyViewed,
  buildRecentlyViewed
} from '@/common/utils/recentClientSearch';
import printPrettyPhoneNumber from '../../../../common/utils/printPrettyPhoneNumber';
import CardLoader from '@/components/CardLoader';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { formDataInstance } from '@/common/axios';
import { DrawerContext } from '@/context/DrawerContext';
import { Link, useParams } from 'react-router';
import { PATH_APP } from '@/routes/paths';
import { ResizeIcon } from '@/common/icons/ResizeIcon';
import { useCustomerProfileQueryParams } from './useCustomerProfileQueryParams';

const useStyles: any = makeStyles((theme: any) => ({
  root: {
    margin: '16px 0 0 16px',
    paddingBottom: 16,
    paddingRight: 8
  },
  loading: {
    height: '100%',
    marginTop: '20%'
  },
  closeBtn: {},
  margin: {
    marginBottom: theme.spacing(1)
  },
  icon: {
    transform: 'translateY(5px)'
  },
  headingMember: {
    fontSize: '1.25rem',
    color: 'green'
  },
  headingNonMember: {
    fontSize: '1.25rem',
    color: 'FireBrick'
  },
  headShot: {
    width: '192px',
    height: '192px',
    borderRadius: '5px',
    borderStyle: 'solid',
    borderColor: theme.palette.primary[500],
    display: 'inline-block'
  },
  customerPhoto: {
    '& .MuiCircularProgress-root': {
      height: '20px !important',
      width: '20px !important'
    }
  },
  restoreLogo: {
    marginLeft: '10px',
    display: 'inline-block',
    width: '25px',
    height: '25px'
  },
  profileSections: {
    padding: '4px !important',
    margin: '4px !important'
  },
  headingContainer: {
    paddingTop: '0 !important'
  },
  primaryDetails: {
    marginTop: 4,
    color: '#308CBF',
    '& img': {
      paddingTop: 4
    },
    '& a': {
      color: '#308CBF'
    },
    '& svg': {
      paddingTop: 4
    }
  },
  fullViewLink: {
    display: 'flex',
    marginTop: 8,
    '& svg': {
      marginTop: -2
    }
  },
  contactInfo: {
    textAlign: 'right',
    paddingRight: 16,
    color: 'black',
    '& p': {
      display: 'inline-block',
      whiteSpace: 'nowrap'
    },
    '& p:nth-child(2)': {
      marginLeft: 16
    }
  }
}));

const getCustomerName = (customerData: any) => {
  let name = '';
  if (customerData?.customer?.firstName) {
    name += customerData?.customer?.firstName || '--';
  }
  if (customerData?.customer?.lastName) {
    if (name.length > 0) {
      name += ' ';
    }
    name += customerData?.customer?.lastName || '--';
  }
  return name;
};

type Props = {
  id?: number;
  drawerMode: boolean;
};

function CustomerProfileContainer({ id, drawerMode }: Props) {
  const params = useParams<{ id?: string }>();
  const customerId = id ?? (params.id ? Number(params.id) : null);

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { hideDrawer } = useContext(DrawerContext);
  const [{ tab, subTab }, setQuery] = useCustomerProfileQueryParams();

  const [customerData, setCustomerData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const getCustomer = async () => {
    if (!customerId) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    let customerData: any = {
      customer: { id: Number(customerId) }
    };

    await formDataInstance
      .get(`${CONFIG.USERS_API_URL}/staff/users/overview`, {
        params: {
          customerId: customerId
        }
      })
      .then(response => {
        customerData = {
          ...response?.data?.data,
          customer: {
            ...response?.data?.data?.customer,
            ...customerData.customer
          },
          isActiveMember: response?.data?.data?.isActiveMember
        };
        addRecentlyViewed(
          buildRecentlyViewed(
            customerData?.customer?.id,
            `${customerData?.customer?.firstName} ${customerData?.customer?.lastName}`,
            customerData?.customer?.email,
            customerData?.customer?.mobilePhone,
            null,
            customerData?.customer?.homeStoreCode,
            customerData?.status?.safetyAlert,
            null
          )
        );
      })
      .catch(e => {
        console.error(e);
        enqueueSnackbar('There was an error loading the Client Info.', {
          variant: 'error'
        });
        setHasError(true);
      })
      .finally(() => {
        setCustomerData(customerData);
        setIsLoading(false);
      });
  };

  const handleCloseDrawer = () => {
    hideDrawer();
    setQuery({ tab: undefined, subTab: undefined }, 'replaceIn');
  };

  useEffect(() => {
    getCustomer();
  }, [customerId]);

  const customerName = getCustomerName(customerData);

  return (
    <Page title={'Client Profile | Client'} className={classes.root}>
      {isLoading ? (
        <div className={classes.loading}>
          <CardLoader />
        </div>
      ) : (
        <Container maxWidth="xl">
          <Grid container spacing={2}>
            <Grid item>
              {drawerMode && (
                <IconButton
                  className={classes.closeBtn}
                  onClick={handleCloseDrawer}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Grid>
            <Grid
              item
              xs={drawerMode ? 11 : 12}
              className={classes.headingContainer}
            >
              {/*<Grid item className={classes.customerPhoto}>
                {customerData?.customer?.photoUrl && (
                  <img
                    src={`https://restore-customer-headshots.s3.amazonaws.com/${customerData?.customer?.photoUrl}`}
                    className={classes.headShot}
                  />
                )}
              </Grid>*/}
              <Grid container xs={12} className={classes.primaryDetails}>
                <Grid item xs={6}>
                  <Typography variant="h4">
                    {!hasError && (
                      <>
                        {customerName}
                        {customerData?.isActiveMember ? (
                          <img
                            src={
                              !customerData?.isMembershipDelinquent
                                ? '/static/brand/restore-logo-blue.svg'
                                : '/static/brand/restore-logo-red.svg'
                            }
                            alt="Restore Logo"
                            title={customerName + ' is an active member'}
                            className={classes.restoreLogo}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    )}
                  </Typography>
                  {drawerMode && (
                    <div>
                      <Link
                        className={classes.fullViewLink}
                        to={{
                          pathname: `${PATH_APP.customer.root}/profile/${customerData?.customer?.id}`,
                          search:
                            tab && tab !== 'info'
                              ? `?tab=${tab}${subTab ? `&subTab=${subTab}` : ''}`
                              : ''
                        }}
                        onClick={hideDrawer}
                      >
                        Open full view <ResizeIcon />
                      </Link>
                    </div>
                  )}
                </Grid>
                <Grid item xs={6} className={classes.contactInfo}>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <LocalPhoneIcon
                        className={classes.icon}
                        fontSize="small"
                      />
                      {'  '}
                      {customerData?.customer?.mobilePhone
                        ? printPrettyPhoneNumber(
                            customerData?.customer?.mobilePhone
                          )
                        : '--'}
                    </Typography>
                    <Typography variant="body2">
                      <EmailIcon className={classes.icon} fontSize="small" />
                      {'  '}
                      {customerData?.customer?.email
                        ? `${customerData?.customer?.email}`
                        : '--'}
                    </Typography>
                    {/*<Typography variant="body2" style={{ paddingLeft: '30px' }}>
                      <ShoppingCartIcon className={classes.icon} fontSize="small" />
                      {'  '}Cart (0)
                      </Typography>*/}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} className={classes.profileSections}>
              <StripeContextProvider>
                <CustomerProfile
                  refetchCustomer={getCustomer}
                  customerAllData={customerData}
                  drawerMode={drawerMode}
                />
              </StripeContextProvider>
            </Grid>
          </Grid>
        </Container>
      )}
    </Page>
  );
}
export default CustomerProfileContainer;
